import { useState } from "react"

import { CheckCircleIcon } from "@heroicons/react/24/solid"
import { Form, useLoaderData, useNavigate } from "react-router-dom"
import { twMerge } from "tailwind-merge"

import { Bee } from "~/src/components/BeeKit"
import { Image } from "~/src/components/Image"
import { Modal } from "~/src/components/Modal"
import { StockIndicator } from "~/src/components/StockIndicator"
import { iname } from "~/src/lib/iname"
import { dataFlag } from "~/src/lib/jsx"

export type StoreItemInventoryVariant = {
  id: string
  variantPhotoId: string
  priceGroupId: string
  title: string
  description: string
  lowQuantity: number
  inventoryCount: number
  inventoryRequired: boolean
  photoUrl?: string
  isStoreKit?: boolean
}

//region Component
export type DashboardStoreInventoryReorderModalProps = {
  open?: boolean
  onClose?: () => void
  title?: string
  variantItems?: StoreItemInventoryVariant[]
  isStoreKit?: boolean
  kitPhotoUrl?: string
}

export function DashboardStoreInventoryReorderModal({
  open = true,
  onClose,
}: DashboardStoreInventoryReorderModalProps) {
  const data = useLoaderData<DashboardStoreInventoryReorderModalProps>()
  const { variantItems, title: productName, isStoreKit, kitPhotoUrl } = data

  const [selectedVariantIds, setSelectedVariantIds] = useState<string[]>([])
  const [quantities, setQuantities] = useState<Record<string, number>>({})
  const navigate = useNavigate()

  const handleClose = () => {
    if (onClose) {
      onClose()
    } else {
      navigate("..", { preventScrollReset: true })
    }
  }

  const toggleVariantSelection = (id: string) => {
    setSelectedVariantIds((prev) => (prev.includes(id) ? prev.filter((v) => v !== id) : [...prev, id]))
  }

  const handleQuantityChange = (variantId: string, value: string) => {
    const quantity = parseInt(value, 10)
    setQuantities((prev) => ({
      ...prev,
      [variantId]: isNaN(quantity) ? 0 : quantity,
    }))
  }

  const isSubmitDisabled =
    selectedVariantIds.length === 0 ||
    selectedVariantIds.some((id) => {
      const minQty = variantItems?.find((v) => v.id === id)?.lowQuantity ?? 1
      return (quantities[id] ?? 0) < minQty
    })
  //endregion

  //region JSX
  return (
    <Modal
      open={open}
      onClose={handleClose}
      className="m-0 max-h-full w-full overflow-auto rounded-none bg-white p-2 px-4 md:flex md:max-h-[calc(100vh-3rem)] md:w-auto md:rounded-lg md:p-6"
      backgroundClassName="w-full place-content-none md:place-content-center"
      usePortal={false}
    >
      <div className="flex h-screen flex-col md:h-auto">
        <Form method="post" className="flex flex-col gap-2 pb-0 md:p-0">
          <hgroup className="flex flex-col gap-2 pb-2">
            {isStoreKit ? (
              <>
                <div className="flex items-start gap-4">
                  <Image
                    className="h-20 w-20 rounded-lg object-cover sm:h-24 sm:w-24 md:h-32 md:w-32"
                    src={kitPhotoUrl}
                    alt={productName || ""}
                  />
                  <div>
                    <h2 className="text-2xl font-medium">Reorder items for {productName}</h2>
                    <p className="text-gray-500">
                      In order for a kit to be considered in stock, all product types must have at least one item in
                      stock.
                    </p>
                  </div>
                </div>
              </>
            ) : (
              <>
                <h2 className="text-2xl font-medium">Reorder</h2>
                <p className="hidden text-gray-500 md:block">
                  Select the items you want to reorder. You can select multiple items at once.
                </p>
              </>
            )}
          </hgroup>

          <div className="mb-28 flex flex-1 flex-col gap-4 overflow-y-auto md:mb-2 md:flex-none md:overflow-visible">
            {variantItems?.map((variant) => {
              const { id, title, description, inventoryRequired, lowQuantity, photoUrl } = variant
              const inventoryCount = inventoryRequired ? variant.inventoryCount : Infinity
              const isSelected = selectedVariantIds.includes(id)
              return (
                <div
                  key={id}
                  onClick={() => toggleVariantSelection(id)}
                  className={twMerge(
                    "group relative flex w-full cursor-pointer gap-4 rounded-lg border-2 bg-white p-4 transition-colors",
                    "border-gray-200 data-[selected]:border-slate-700 md:flex-row"
                  )}
                  {...dataFlag(isSelected, "selected")}
                >
                  {/* Checkmark in upper-left */}
                  <div className="pointer-events-none absolute left-2 top-2 z-10 opacity-0 transition-opacity group-data-[selected]:opacity-100">
                    <CheckCircleIcon className="h-6 w-6 text-slate-700" />
                  </div>

                  <img
                    className="h-20 w-20 rounded-lg object-contain md:h-full md:w-52 md:object-cover md:object-center"
                    src={photoUrl}
                  />

                  <div className="flex flex-1 flex-col gap-2">
                    <div className="text-base font-medium md:text-lg md:font-normal">{title ?? productName}</div>
                    <div className="text-sm font-normal text-gray-500 md:text-base">{description}</div>
                    <div className="gap-2">
                      {inventoryCount == null ? (
                        <></>
                      ) : (
                        <StockIndicator className="ml-auto text-xl md:text-base" quantity={inventoryCount} />
                      )}
                    </div>

                    <fieldset>
                      <section className="mt-1 flex flex-col justify-start gap-1">
                        <div className="text-sm font-medium text-gray-700">Quantity</div>
                        <Bee.Input
                          type="number"
                          name={isSelected ? iname()[id].quantity : undefined}
                          className="w-full px-4 py-2 text-sm"
                          min={lowQuantity}
                          placeholder={`Minimum ${lowQuantity}`}
                          onClick={(e) => isSelected && e.stopPropagation()}
                          onChange={(e) => handleQuantityChange(id, e.target.value)}
                        />
                      </section>
                    </fieldset>
                  </div>
                </div>
              )
            })}
          </div>

          <div className="flex flex-col gap-3 p-4 md:p-0">
            <Bee.Button
              type="submit"
              className="fixed inset-x-4 bottom-24 drop-shadow-sm md:static md:w-auto"
              disabled={isSubmitDisabled}
            >
              Add to project request
            </Bee.Button>
          </div>
        </Form>
      </div>
    </Modal>
  )
}
//endregion
